// OrderedBuffer allows adding items and retrieving them in their correct order, even if they were added out of order.
// If there is no item with the next sequential ID, it will wait until that item is added before returning any items.
export class OrderedBuffer<T> {
    private lastTimingRecord: [number, number, number] | null = null
    private buffer = new Map<number, T>()
    private timingDifferences: number[] = []
    private knownSamplingPeriods: number[] = []
    private MAX_TIMING_SAMPLES = 10

    public nextId: number = 1
    public maxId: number = 1
    public knownGaps: Array<number> = []
    public knownSamplingPeriod: number = Infinity
    public numberOfPointsProcessed: number = 0
    public unloadedStartTime: number | null = null

    // Add a new item to the buffer. Returns any in-order items ready for processing.
    public add(id: number, item: T): T[] {
        this.buffer.set(id, item)
        const ready: T[] = []

        while (this.buffer.has(this.nextId)) {
            ready.push(this.buffer.get(this.nextId)!)
            this.buffer.delete(this.nextId)
            this.nextId++
        }

        return ready
    }

    public setMaxId(maxId: number) {
        this.maxId = maxId
    }

    public isCompleted() {
        return this.nextId > this.maxId
    }

    public updateUnloadedStartTime(time: number) {
        this.unloadedStartTime = time
    }

    public clear() {
        this.buffer.clear()
        this.maxId = 1
        this.nextId = 1
    }

    public addTimingRecord(startTimeMs: number, endTimeMs: number, numberOfPoints: number) {
        let samplingPeriod = -1

        if (numberOfPoints > 1) {
            samplingPeriod = (endTimeMs - startTimeMs) / (numberOfPoints - 1)
            this.knownSamplingPeriods.push(samplingPeriod)

            if (this.knownSamplingPeriods.length > this.MAX_TIMING_SAMPLES) {
                this.knownSamplingPeriods.shift()
            }

        } else if (this.lastTimingRecord !== null) {
            // In the case where we are just getting one value at a time, we need to track the timing differences and come up with a reasonable sampling period.
            const difference = startTimeMs - this.lastTimingRecord[1]

            if (difference > 0) {
                this.timingDifferences.push(difference)
            }

            if (this.timingDifferences.length > this.MAX_TIMING_SAMPLES) {
                this.timingDifferences.shift()
            }
        }

        // Determine the most appropriate sampling period
        if (this.knownSamplingPeriods.length > 0) {
            this.knownSamplingPeriod = this.knownSamplingPeriods[0]
        } else if (this.timingDifferences.length > 0) {
            this.knownSamplingPeriod = this.median(this.timingDifferences)
        }

        if (this.lastTimingRecord !== null) {
            const lastSamplingPeriod = (this.lastTimingRecord[1] - this.lastTimingRecord[0]) / (this.lastTimingRecord[2] - 1)
            const lastEndTime = this.lastTimingRecord[1]

            // If we are pretty sure there is a gap here, add the gap index (with some wiggle room)
            if (startTimeMs - lastEndTime > lastSamplingPeriod * 1.05) {
                this.knownGaps.push(this.numberOfPointsProcessed)
            }
        }

        this.numberOfPointsProcessed += numberOfPoints
        this.lastTimingRecord = [startTimeMs, endTimeMs, numberOfPoints]
    }

    private median(values: number[]): number {
        const sorted = [...values].sort((a, b) => a - b)
        const mid = Math.floor(sorted.length / 2)

        return sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid]
    }
}
